/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Modal from 'apollo-react/components/Modal';
import Pagination from 'apollo-react/components/Pagination';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import TextField from 'apollo-react/components/TextField';
import SearchIcon from 'apollo-react-icons/Search';
import classnames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { clearErrors } from '../../actions/ErrorActions';
import {
  clearPredictions,
  getDictionary,
  getPrediction,
} from '../../actions/RegulatoryTermsActions';
import { addAIGeneratedContentToast } from '../../actions/ToastActions';
import { MEDDRA_DICTIONARY_ROWS_PER_PAGE } from '../../constants/Common';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { getAppSettings } from '../../selectors/AppConfigsSelectors';
import { checkTrue, getUITA } from '../../utils/commonUtils';
import { isEmpty } from '../../utils/ramdaUtils';
import PredictionTable from '../PredictionTable/PredictionTable';
import RegulatoryTermsTree from '../RegulatoryTermsTree';

const useStyles = makeStyles({
  zoomDialogPaperFullWidth: {
    maxWidth: '80%',
  },
  zoomDialogMobilePaperFullWidth: {
    maxWidth: '90%',
    margin: 0,
    width: 'calc(100% - 32px)',
  },
  form: {
    paddingBottom: 20,
    borderBottom: '1px solid #e9e9e9',
  },
  inlineCtr: {
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    '& > label': {
      width: 180,
    },
  },
  mobileInlineCtr: {
    flexDirection: 'column',
    alignItems: 'baseline',
  },
  label: {
    color: '#444444',
    display: 'block',
    fontSize: 14,
    lineHeight: 1.5,
    transformOrigin: 'top left',
    width: 180,
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    '& > [class*=MuiTextField-root]': {
      display: 'block',
      '& input[type=text]': {
        paddingLeft: 40,
      },
    },
  },
  mobileInputWrapper: {
    marginTop: 8,
  },
  searchIconButton: {
    position: 'absolute',
    paddingTop: '8px !important',
    paddingLeft: '10px !important',
    color: '#0768fd',
    fontSize: '1.4rem',
    cursor: 'pointer',
  },
  disabledSearchIconButton: {
    color: '#999999',
    cursor: 'default',
  },
  tableCtr: {
    marginTop: 16,
    height: 416,
    overflow: 'auto',
    flex: 1,
  },
  predictionControl: {
    width: '50%',
    margin: 16,
    marginBottom: 40,
    marginRight: 0,
    height: 400,
  },
  mobilePredictionControl: {
    width: '100%',
    marginBottom: 40,
    marginTop: 40,
    height: 440,
  },
  dictionaryCtr: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  mobileDictionaryCtr: {
    display: 'flex',
    justifyContent: 'space-around',
    flexDirection: 'column',
    '& [class*="MuiTreeItem-label"] > span': {
      overflow: 'unset',
    },
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 16,
  },
  mobileButton: {
    paddingLeft: 0,
  },
  muiDialogContent: {
    '& [class*="MuiDialogActions"]': {
      marginBottom: 20,
      marginTop: 5,
    },
  },
});

const RegulatoryTermsZoomDialog = ({
  open,
  type,
  defaultSearchText,
  searchByItems = [],
  searchOptionItems = [],
  onSelect,
  onClose,
  predictionText,
  termHierarchy,
  regulatoryTerms,
  predictionCode,
  mappingFieldName,
}) => {
  const [selectedItem, setSelectedItem] = useState();
  const [buttonProps, setButtonProps] = useState([]);
  const [selectedPath, setSelectedPath] = useState();
  const [searchBy, setSearchBy] = useState();
  const [isReverse, setIsReverse] = useState(false);
  const [searchOptions, setSearchOptions] = useState();
  const [searchText, setSearchText] = useState('');
  const [isEmptySearchText, setIsEmptySearchText] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const [paginationPage, setPaginationPage] = useState(0);
  const [paginationCount, setPaginationCount] = useState(0);
  const [isWarningModalOpen, setWarningModalOpen] = useState(false);

  const { 'AzurePredictionDataService/Enabled': translationServiceEnabledFlag } = useSelector(
    getAppSettings
  );

  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const { isMobileDevice } = useWindowDimensions();

  useEffect(() => {
    setDefaultValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSearchText]);

  const showPredictionSection =
    predictionCode && type === 'IMDRF' && checkTrue(translationServiceEnabledFlag);

  useEffect(() => {
    if (showPredictionSection) {
      setButtonProps([
        {
          label: translateString('Cancel'),
          onClick: onClose,
        },
        {
          label: translateString('Run Assistant'),
          variant: 'secondary',
          onClick: handlePredictionButtonClick,
          'ui-ta': 'Run Assistant Button',
        },
        {
          label: translateString('Select'),
          disabled: !selectedItem?.selectable,
          onClick: handleIMDRFSelect,
        },
      ]);
    } else {
      setButtonProps([
        {
          label: translateString('Cancel'),
          onClick: onClose,
        },
        {
          label: translateString('Select'),
          disabled: !selectedItem?.selectable,
          onClick: type === 'IMDRF' ? handleIMDRFSelect : handleSelectRepository,
        },
      ]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  useEffect(() => {
    if (!open) {
      resetPagination();
    }
  }, [open]);

  const resetPagination = () => setPaginationPage(0);

  const handleIsReverseTree = () => setIsReverse(type === 'MEDDRA' && searchBy !== 'SOC');

  const setDefaultValues = () => {
    const checkedSearchByItem = searchByItems.find((item) => item.checked);
    const checkedSearchOptionItem = searchOptionItems.find((item) => item.checked);

    checkedSearchByItem && setSearchBy(checkedSearchByItem.value);
    checkedSearchOptionItem && setSearchOptions(checkedSearchOptionItem.value);
    setSearchText(defaultSearchText || '');
    setIsReverse(!isEmpty(defaultSearchText));
  };

  const handleClose = () => {
    dispatch(clearErrors((e) => !e.isTopBanner));
    setDefaultValues();
    setIsEmptySearchText(false);
    onClose();
  };

  const handleRepositoryItemClick = (item, path) => {
    setSelectedItem(item);
    setSelectedPath(path);
  };

  const handleSelectRepository = () => {
    handleClose();
    onSelect({ selectedItem, selectedPath });
  };

  const handleIMDRFSelect = () => {
    if (selectedItem.showWarningModal) {
      setWarningModalOpen(true);
      return;
    }
    handleSelectRepository();
  };

  const handleWarningModalSelect = () => {
    setWarningModalOpen(false);
    handleSelectRepository();
  };

  const handleChangeSearchBy = (e) => {
    setSearchBy(e.target.value);
  };

  const handleChangeSearchOptions = (e) => {
    setSearchOptions(parseInt(e.target.value));
  };

  const handleChangeSearchText = (e) => {
    isEmptySearchText && setIsEmptySearchText(false);
    setSearchText(e.target.value);
  };

  const changeSearchText = (value) => {
    isEmptySearchText && setIsEmptySearchText(false);
    setSearchText(value);
  };

  const handleSubmitSearch = (e) => {
    !_.isNil(e) && e.preventDefault();

    resetPagination();

    if (isEmpty(searchText)) {
      setIsEmptySearchText(true);
      return;
    }

    handleIsReverseTree();

    handleRepositoryItemClick(null, null);
    dispatch(
      getDictionary(
        type,
        {
          HierarchyId: '',
          TextSearchOption: searchOptions,
          TermHierarchy: termHierarchy,
          Dictionary: regulatoryTerms,
          SearchText: searchText,
          SearchBy: searchBy,
          ShowAll: isEmpty(searchText),
        },
        true
      )
    );

    setAllExpanded(true);
  };

  const handleDisplayAll = () => {
    resetPagination();
    setIsReverse(false);
    handleRepositoryItemClick(null, null);
    dispatch(
      getDictionary(type, {
        HierarchyId: '',
        TextSearchOption: searchOptions,
        TermHierarchy: termHierarchy,
        Dictionary: regulatoryTerms,
        SearchText: searchText,
        SearchBy: searchBy,
        ShowAll: true,
      })
    );

    setAllExpanded(true);
  };

  const handlePredictionButtonClick = () => {
    dispatch(getPrediction(predictionText, mappingFieldName))
      .then(() => {
        predictionText && dispatch(addAIGeneratedContentToast());
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
  };

  const props = {
    type,
    searchBy,
    searchOptions,
    termHierarchy,
    predictionText,
    regulatoryTerms,
    resetPagination,
    handleIsReverseTree,
    handleRepositoryItemClick,
    getDictionary,
    setAllExpanded,
    changeSearchText,
    clearPredictions,
  };

  const inlineClassName = !isMobileDevice
    ? classes.inlineCtr
    : classnames(classes.inlineCtr, classes.mobileInlineCtr);

  return (
    <Modal
      open={open}
      title={`${translateString(type)} ${translateString('Dictionary')}`}
      classes={{
        paperFullWidth: isMobileDevice
          ? classes.zoomDialogMobilePaperFullWidth
          : classes.zoomDialogPaperFullWidth,
        paper: classes.muiDialogContent,
      }}
      PaperProps={{
        'ui-ta': getUITA('SSRegulatoryTermsZoomDialog'),
      }}
      buttonProps={buttonProps}
      fullWidth
      disableEnforceFocus
      disableBackdropClick
      disableEscapeKeyDown
      onClose={handleClose}
    >
      <span>
        <form onSubmit={handleSubmitSearch} className={classes.form} ui-ta="Search Criteria">
          <div className={inlineClassName}>
            <RadioGroup
              label={translateString('Search By')}
              aria-label="label"
              name="searchBy"
              value={searchBy}
              onChange={handleChangeSearchBy}
              row
            >
              {searchByItems.map((item) => (
                <Radio value={item.value} label={item.label} key={item.label} />
              ))}
            </RadioGroup>
          </div>
          <div className={inlineClassName}>
            <RadioGroup
              label={translateString('Search Options')}
              aria-label="label"
              name="searchOptions"
              value={searchOptions}
              onChange={handleChangeSearchOptions}
              row
            >
              {searchOptionItems.map((item) => (
                <Radio value={item.value} label={item.label} key={item.label} />
              ))}
            </RadioGroup>
          </div>
          <div className={inlineClassName}>
            <div className={classes.label}>{translateString('Search')}</div>
            <span
              className={
                isMobileDevice
                  ? classnames(classes.inputWrapper, classes.mobileInputWrapper)
                  : classes.inputWrapper
              }
            >
              <TextField
                margin="none"
                name="searchText"
                className={classes.formWidth}
                placeholder={translateString('Search')}
                value={searchText}
                onChange={handleChangeSearchText}
                error={isEmptySearchText}
                helperText={isEmptySearchText ? translateString('Search text is required') : null}
                inputProps={{
                  style: { width: 350 },
                }}
              />
              <span
                color="primary"
                className={classnames(
                  classes.searchIconButton,
                  _.isEmpty(searchText) ? classes.disabledSearchIconButton : ''
                )}
                onClick={(e) => searchText && handleSubmitSearch(e)}
                ui-ta="Search Button"
                role="button"
              >
                <SearchIcon />
              </span>
            </span>
            <Button
              className={isMobileDevice && classes.mobileButton}
              variant="text"
              onClick={handleDisplayAll}
            >
              {translateString('Display All')}
            </Button>
          </div>
        </form>
        <div className={isMobileDevice ? classes.mobileDictionaryCtr : classes.dictionaryCtr}>
          <div className={classes.tableCtr} ui-ta="Result Tree">
            <RegulatoryTermsTree
              type={type}
              termHierarchy={termHierarchy}
              onSelectItem={handleRepositoryItemClick}
              handleSelectRepository={handleSelectRepository}
              handleIMDRFSelect={handleIMDRFSelect}
              allExpanded={allExpanded}
              searchText={searchText}
              isReverse={isReverse}
              regulatoryTerms={regulatoryTerms}
              paginationPage={paginationPage}
              setPaginationCount={setPaginationCount}
              setWarningModalOpen={setWarningModalOpen}
            />
          </div>
          {showPredictionSection && (
            <div
              className={
                isMobileDevice ? classes.mobilePredictionControl : classes.predictionControl
              }
              ui-ta="Prediction Table"
            >
              <PredictionTable isMobileDevice={isMobileDevice} {...props} />
            </div>
          )}
        </div>
        {type === 'MEDDRA' && (
          <div className={classes.pagination}>
            <Pagination
              count={paginationCount}
              rowsPerPage={MEDDRA_DICTIONARY_ROWS_PER_PAGE}
              page={paginationPage}
              onChangePage={setPaginationPage}
              pageText={translateString('Page')}
              ofText={translateString('of')}
            />
          </div>
        )}
      </span>
      <Modal
        open={isWarningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        variant="warning"
        title={translateString('Warning')}
        message={translateString(
          'A more appropriate IMDRF code may exist in the lower nodes of this Annex. Click OK to continue or Cancel to select a different code.'
        )}
        disableBackdropClick
        disableEscapeKeyDown
        buttonProps={[
          {
            variant: 'secondary',
            label: translateString('Cancel'),
          },
          {
            variant: 'primary',
            label: translateString('Ok'),
            onClick: () => handleWarningModalSelect(),
          },
        ]}
        ui-ta={getUITA('IMDRFWarningModal')}
      />
    </Modal>
  );
};

export default RegulatoryTermsZoomDialog;
